/**
 * Pure resolution of the StructuralPlanning right-drawer content from the selection state.
 * Replaces the mode-keyed if-chain (3D) and the element-type-keyed block list of the former
 * FloorplanElementForm (2D) with one ordered decision, so the drawer is a function of
 * (mode, selection, subSelection, view) and nothing else:
 *
 * - element forms only render with a matching selection (no more empty forms from `[]`-truthy
 *   guards); without one the drawer shows an explicit empty state;
 * - exactly one pane resolves (the 2D block list could stack multiple forms);
 * - a selection in neutral mode resolves to its element's form (the selection survives page and
 *   2D/3D switches, so the drawer must be able to render it without a mode).
 */
import { isMultiSelectionInMode } from '@editor/stores/selectionStore/reconcile'

type DrawerView = '2d' | '3d'

type SPDrawerContent =
  | { kind: 'multi-edit'; guids: string[] }
  /**
   * `fromPrimaryOpening` marks the legacy "opening as primary selection" path (openings mode),
   * where the wall is derived from the opening; otherwise the wall is the primary selection and
   * the opening the sub-selection (draw-openings / edit-openings-3d).
   */
  | {
      kind: 'opening-form'
      wallGuid: string | null
      openingGuid: string | null
      fromPrimaryOpening: boolean
    }
  | { kind: 'stiffening-selection' }
  | { kind: 'position-form'; positionGuid: string }
  | { kind: 'wall-form'; wallGuid: string | null }
  | { kind: 'roof-slab-form'; guids: string[] }
  | { kind: 'load-list' }
  | { kind: 'building-form' }
  | { kind: 'wall-rip-form'; wallGuid: string | null }
  | { kind: 'wall-lintel-form'; lintelGuid: string | null }
  | { kind: 'beam-form'; guid: string | null }
  | { kind: 'column-form'; guid: string | null }
  | { kind: 'purlin-form'; guid: string }
  | { kind: 'vertical-roof-form'; guid: string | null }
  | { kind: 'vertical-slabs-form'; guid: string | null }
  | { kind: 'slab-form'; guid: string | null }
  | { kind: 'roof-form'; guid: string | null }
  | { kind: 'foundation-panel'; guid: string | null }
  | { kind: 'empty'; reason: 'no-selection' | 'no-tool' | 'unsupported' }

interface ResolveDrawerInput {
  mode: EditorMode
  view: DrawerView
  selection: SceneElementSelection | null
  /** Element type of the (single) selection, resolved from the selection or the model. */
  selectionElementType: PlanarSelectedElementType | undefined
  /** Opening sub-selection guid (`draw-openings` / `edit-openings-3d`). */
  activeOpeningGuid: string | null
}

const noSelection = { kind: 'empty', reason: 'no-selection' } as const
const noTool = { kind: 'empty', reason: 'no-tool' } as const
const unsupported = { kind: 'empty', reason: 'unsupported' } as const

/** The form belonging to a selected element of the given type (mode-independent). */
const contentForElementType = (
  elementType: PlanarSelectedElementType,
  guid: string,
  view: DrawerView,
): SPDrawerContent => {
  switch (elementType) {
    case 'inner_walls':
    case 'outer_walls':
      return { kind: 'wall-form', wallGuid: guid }
    case 'beams':
      return { kind: 'beam-form', guid }
    case 'columns':
      return { kind: 'column-form', guid }
    case 'purlins':
      return { kind: 'purlin-form', guid }
    case 'vertical_slabs':
      return { kind: 'vertical-slabs-form', guid }
    case 'vertical_roof_slabs':
      return { kind: 'vertical-roof-form', guid }
    case 'slabs':
    case 'roof_slabs':
      // In the floorplan the roof slab has its dedicated 2D form; in 3D both use the slab pane.
      return view === '2d'
        ? elementType === 'roof_slabs'
          ? { kind: 'roof-form', guid }
          : unsupported
        : { kind: 'roof-slab-form', guids: [guid] }
    case 'rip':
    case 'lintel':
    case 'rips':
    case 'lintels':
      return { kind: 'position-form', positionGuid: guid }
    case 'opening':
      return { kind: 'opening-form', wallGuid: null, openingGuid: guid, fromPrimaryOpening: true }
    case 'foundation':
      return view === '2d' ? unsupported : { kind: 'foundation-panel', guid }
    default:
      return unsupported
  }
}

const resolveDrawerContent = ({
  mode,
  view,
  selection,
  selectionElementType,
  activeOpeningGuid,
}: ResolveDrawerInput): SPDrawerContent => {
  const planarSelection = selection?.type === 'planar' ? selection : null
  const selectedGuids = planarSelection?.guids ?? []
  const isMulti = isMultiSelectionInMode(selection, mode)
  const singleGuid = planarSelection && !isMulti ? selectedGuids[0] ?? null : null

  // A multi-selection always shows the multi edit pane, whether it entered through the
  // multi-edit mode (3D marquee/shift) or as a plain multi-selection (2D marquee). With no
  // elements left (last one removed, or ESC cleared the selection) the pane has nothing to
  // edit — show the empty state instead of a dangling multi-edit panel.
  if (mode === 'multi-edit' || isMulti)
    return selectedGuids.length ? { kind: 'multi-edit', guids: selectedGuids } : noSelection

  // Mode-bound panes that behave the same in both views.
  switch (mode) {
    case 'openings':
      return {
        kind: 'opening-form',
        wallGuid: null,
        openingGuid: singleGuid,
        fromPrimaryOpening: true,
      }
    case 'selection':
      return { kind: 'stiffening-selection' }
    case 'rip':
    case 'lintel':
    case 'position':
      return singleGuid ? { kind: 'position-form', positionGuid: singleGuid } : noSelection
    case 'inner_walls':
    case 'outer_walls':
    case 'stiffening':
      return singleGuid ? { kind: 'wall-form', wallGuid: singleGuid } : noSelection
    case 'slabs':
    case 'roof_slabs':
      return selectedGuids.length ? { kind: 'roof-slab-form', guids: selectedGuids } : noSelection
    case 'line-load':
    case 'point-load':
    case 'area-load':
      return { kind: 'load-list' }
    case 'draw-openings':
    case 'edit-openings-3d':
      return {
        kind: 'opening-form',
        wallGuid: singleGuid,
        openingGuid: activeOpeningGuid,
        fromPrimaryOpening: false,
      }
    case 'building':
      return { kind: 'building-form' }
    case 'wall-rip':
      return { kind: 'wall-rip-form', wallGuid: singleGuid }
    case 'wall-lintel':
      // The lintel tool is 3D-only; the floorplan never rendered a pane for it (in particular
      // not the wall form of the selected wall).
      return view === '3d' ? { kind: 'wall-lintel-form', lintelGuid: singleGuid } : noTool
    default:
      break
  }

  if (view === '2d') {
    // The floorplan drawer is element-type driven: the selected element wins, drawing tools
    // provide the fallback pane while nothing is selected yet.
    if (singleGuid && selectionElementType)
      return contentForElementType(selectionElementType, singleGuid, view)

    switch (mode) {
      case 'draw-walls':
        return { kind: 'wall-form', wallGuid: singleGuid }
      case 'draw-beams':
        return { kind: 'beam-form', guid: singleGuid }
      case 'draw-columns':
        return { kind: 'column-form', guid: singleGuid }
      case 'draw-vertical-slabs':
        return { kind: 'vertical-slabs-form', guid: singleGuid }
      case 'draw-vertical-roofs':
        return { kind: 'vertical-roof-form', guid: singleGuid }
      case 'draw-purlins':
        return singleGuid ? { kind: 'purlin-form', guid: singleGuid } : noTool
      default:
        return noTool
    }
  }

  switch (mode) {
    case 'draw-vertical-roofs':
      return singleGuid ? { kind: 'vertical-roof-form', guid: singleGuid } : noSelection
    case 'draw-vertical-slabs':
      return singleGuid ? { kind: 'vertical-slabs-form', guid: singleGuid } : noSelection
    case 'draw-slabs':
      return { kind: 'slab-form', guid: singleGuid }
    case 'draw-roofs':
      return { kind: 'roof-form', guid: singleGuid }
    case 'draw-purlins':
      return singleGuid ? { kind: 'purlin-form', guid: singleGuid } : noSelection
    case 'draw-beams':
      return singleGuid ? { kind: 'beam-form', guid: singleGuid } : noSelection
    case 'draw-walls':
      // Wall drawing is a floorplan tool; the 3D chain never rendered a pane for it.
      return noTool
    case 'beams':
      return singleGuid ? { kind: 'beam-form', guid: singleGuid } : noSelection
    case 'columns':
      return singleGuid ? { kind: 'column-form', guid: singleGuid } : noSelection
    case 'purlins':
      return singleGuid ? { kind: 'purlin-form', guid: singleGuid } : noSelection
    case 'vertical_roof_slabs':
      return singleGuid ? { kind: 'vertical-roof-form', guid: singleGuid } : noSelection
    case 'vertical_slabs':
      return singleGuid ? { kind: 'vertical-slabs-form', guid: singleGuid } : noSelection
    case 'foundation':
      return { kind: 'foundation-panel', guid: singleGuid }
    default:
      break
  }

  // No mode-bound pane (neutral or a mode without a drawer): render the selected element's form
  // so a selection kept across a page or view switch stays visible.
  if (singleGuid && selectionElementType)
    return contentForElementType(selectionElementType, singleGuid, view)

  return noTool
}

export default resolveDrawerContent
export type { SPDrawerContent, ResolveDrawerInput, DrawerView }
